import { useEffect, useState } from "react";
import { modelLabel } from "../../../shared/models";
import type { AgentDTO, AgentStatus, AgentSummaryStatsDTO, ConnectorDTO, CostSplit } from "../../../shared/types";
import { accessLine, connectorBrand, NewAgentModal, ownersLine } from "../../components/AgentEditor";
import { BrandIcon } from "../../components/BrandIcon";
import { CostBreakdown } from "../../components/CostBreakdown";
import { ShowArchived, useShowArchived } from "../../components/ShowArchived";
import { Avatar, Badge, Button, PageHeader, timeAgo } from "../../components/ui";
import { get } from "../../lib/api";
import { Link, navigate } from "../../lib/router";
import { agentStatusWords } from "../../lib/schedules";

const money = (usd: number) => `$${usd < 10 ? usd.toFixed(2) : usd.toFixed(0)}`;

const STATUS_STYLE: Record<AgentStatus, { dot: string; text: string }> = {
  thinking: { dot: "bg-info animate-pulse", text: "text-info" },
  waiting: { dot: "bg-warning", text: "text-warning" },
  blocked: { dot: "bg-danger", text: "text-danger" },
  failing: { dot: "bg-danger", text: "text-danger" },
  scheduled: { dot: "bg-scheduled", text: "text-scheduled" },
  sleeping: { dot: "bg-faint", text: "text-muted" },
  disabled: { dot: "bg-faint", text: "text-faint" },
};

/** The agent's state right now: thinking, waiting on a person, scheduled with its next run beside it, or asleep. */
export function AgentStatusChip({ status, nextRunAt = null, className = "" }: { status: AgentStatus; nextRunAt?: string | null; className?: string }) {
  const style = STATUS_STYLE[status];
  const words = agentStatusWords(status, nextRunAt);
  return (
    <span title={words.hint} className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-medium ${style.text} ${className}`}>
      <span className={`size-2 rounded-full ${style.dot}`} />
      {words.label}
    </span>
  );
}

function ConnectorMarks({ connectors, max = 6 }: { connectors: ConnectorDTO[]; max?: number }) {
  if (connectors.length === 0) return <span className="text-xs text-faint">No connectors</span>;
  return (
    <span className="flex items-center gap-1.5">
      <span className="flex -space-x-1.5">
        {connectors.slice(0, max).map((c) => (
          <span key={c.id} title={c.name} className="grid size-6 place-items-center rounded-full bg-raised shadow-sm ring-2 ring-surface">
            <BrandIcon brand={connectorBrand(c)} className="size-3.5" />
          </span>
        ))}
      </span>
      {connectors.length > max && <span className="text-xs text-muted">+{connectors.length - max}</span>}
    </span>
  );
}

function Stat({ label, value, breakdown }: { label: string; value: string | number; breakdown?: CostSplit }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-tiny text-muted">{label}</div>
      <div className="flex min-w-0 items-center gap-1 text-sm font-semibold tabular-nums">
        <span className="min-w-0 truncate">{value}</span>
        {breakdown && <CostBreakdown billedUsd={breakdown.billedUsd} estimatedUsd={breakdown.estimatedUsd} />}
      </div>
    </div>
  );
}

export function AdminAgents() {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [stats, setStats] = useState<Record<string, AgentSummaryStatsDTO>>({});
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useShowArchived();

  const load = () => {
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
    get<Record<string, AgentSummaryStatsDTO>>("/api/agents/stats").then(setStats, () => {});
  };
  useEffect(load, []);

  const primary = agents.find((a) => a.isPrimary);
  // Top-level specialists, each followed by its sub-agents; the archived ones only when asked for.
  const archivedCount = agents.filter((a) => a.archivedAt).length;
  const nonPrimary = agents.filter((a) => !a.isPrimary && (showArchived || !a.archivedAt));
  const known = new Set(nonPrimary.map((a) => a.id));
  const tree = (parentId: string | null, depth = 0): AgentDTO[] =>
    depth > 10 ? [] : nonPrimary.filter((a) => (a.parentAgentId && known.has(a.parentAgentId) ? a.parentAgentId : null) === parentId).flatMap((a) => [a, ...tree(a.id, depth + 1)]);
  const specialists = tree(null);
  const parentOf = (a: AgentDTO) => nonPrimary.find((p) => p.id === a.parentAgentId);
  const parentName = (a: AgentDTO) => parentOf(a)?.name;
  // A sub-agent reaches its parent's connectors as well as its own.
  const connectorIdsOf = (a: AgentDTO) => [...new Set([...a.connectorIds, ...(parentOf(a)?.connectorIds ?? [])])];
  const childCount = (a: AgentDTO) => nonPrimary.filter((c) => c.parentAgentId === a.id).length;
  const enabledConnectors = connectors.filter((c) => c.enabled);
  const s = (id: string): AgentSummaryStatsDTO =>
    stats[id] ?? { runs: 0, costUsd: 0, billedUsd: 0, estimatedUsd: 0, active: 0, memories: 0, lastRunAt: null, status: "sleeping", schedules: 0, nextRunAt: null, pendingApprovals: 0, monthSpendUsd: 0 };

  return (
    <div className="pb-10">
      <PageHeader
        title="Agents"
        subtitle="Everyone talks to Pandito. It uses any connector the person may use, or hands the work to a specialist below."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>New agent</Button>}
      />

      <div className="space-y-8 px-4 md:px-6">
        {primary && (
          <Link to={`/admin/agents/${primary.id}`} className="card group block overflow-hidden transition hover:shadow-md">
            <div className="flex flex-col gap-5 bg-gradient-to-br from-haze/70 via-haze-soft to-white p-5 sm:flex-row sm:items-center dark:from-haze/10 dark:via-zinc-900 dark:to-zinc-900">
              <img src={primary.avatarUrl ?? "/favicon.png"} alt="" className="size-16 shrink-0 rounded-2xl object-cover shadow-sm ring-1 ring-line" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-heading font-semibold tracking-tight">{primary.name}</h2>
                  <Badge tone="blue">Primary agent</Badge>
                  <AgentStatusChip status={s(primary.id).status} nextRunAt={s(primary.id).nextRunAt} />
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-muted">{primary.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
                  <span className="flex items-center gap-2">
                    <ConnectorMarks connectors={enabledConnectors} max={10} /> All connectors
                  </span>
                  <span>Hands work to {specialists.filter((a) => a.enabled && !a.parentAgentId).length} agents</span>
                  <span>{modelLabel(primary.model)}</span>
                </div>
              </div>
              <div className="grid shrink-0 grid-cols-3 gap-4 rounded-xl bg-surface/70 px-4 py-3 ring-1 ring-line sm:w-72">
                <Stat label="Runs · 30d" value={s(primary.id).runs} />
                <Stat label="Spend · 30d" value={money(s(primary.id).costUsd)} breakdown={s(primary.id)} />
                <Stat label="Memories" value={s(primary.id).memories} />
              </div>
            </div>
          </Link>
        )}

        <section>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-heading font-semibold tracking-tight">Specialists</h2>
            {archivedCount > 0 && <ShowArchived on={showArchived} onChange={setShowArchived} count={archivedCount} />}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {specialists.map((a) => {
              const st = s(a.id);
              return (
                <div key={a.id} role="button" tabIndex={0} onClick={() => navigate(`/admin/agents/${a.id}`)} onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); navigate(`/admin/agents/${a.id}`); } }} className={`card flex min-w-0 cursor-pointer flex-col p-4 text-left transition hover:shadow-md ${a.parentAgentId ? "border-l-4 border-l-haze-deep" : ""}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={a.name} src={a.avatarUrl} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{a.name}</span>
                        {a.archivedAt && <Badge tone="amber">Archived</Badge>}
                        <AgentStatusChip status={st.status} nextRunAt={st.nextRunAt} className="ml-auto" />
                      </div>
                      <div className="truncate text-tiny text-faint">
                        {parentName(a) ? `Sub-agent of ${parentName(a)}` : <span className="font-mono">{a.slug}</span>}
                        {childCount(a) > 0 && ` · ${childCount(a)} sub-agent${childCount(a) === 1 ? "" : "s"}`}
                      </div>
                      <div className="truncate text-tiny text-faint">
                        {ownersLine(a)} · {accessLine(a)}
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 line-clamp-2 min-h-10 text-sm text-muted">{a.description || "No description"}</p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <ConnectorMarks connectors={enabledConnectors.filter((c) => connectorIdsOf(a).includes(c.id))} />
                    <span className="truncate text-tiny text-faint">{modelLabel(a.model)}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-4 gap-2 border-t border-line pt-3">
                    <Stat label="Runs" value={st.runs} />
                    <Stat label="Spend" value={money(st.costUsd)} breakdown={st} />
                    <Stat label="Memories" value={st.memories} />
                  </div>
                  <div className="mt-2 text-tiny text-faint">
                    {st.nextRunAt ? `Next run ${new Date(st.nextRunAt).toLocaleString()}` : st.lastRunAt ? `Last run ${timeAgo(st.lastRunAt)}` : "No runs yet"}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {creating && (
        <NewAgentModal
          connectors={connectors}
          onClose={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            navigate(`/admin/agents/${a.id}`);
          }}
        />
      )}
    </div>
  );
}
